// Design tokens — themes, accents, density, font stacks.
//
// Ported from the legacy `theme.jsx` (which exposed these as `window.X`).
// Pure data only — no DOM, no RN — so apps/mobile can consume directly.
// Web font stack is broad (Inter + Noto Armenian/Hebrew); on RN we'll
// register fonts via expo-font and reference simpler family names — the
// `FONT_STACK` strings here are mostly useful to apps/web.

// ─── Keys ────────────────────────────────────────────────────────────

export type ThemeName = 'light' | 'dark';
export type AccentKey = 'amber' | 'coral' | 'electric' | 'emerald' | 'violet';
export type DensityKey = 'compact' | 'comfortable';
export type HomeVariantKey = 'control' | 'editorial' | 'intel';

// ─── Token shapes ────────────────────────────────────────────────────

export interface ThemeTokens {
  bg: string;
  surface: string;
  surface2: string;
  surfaceHover: string;
  border: string;
  borderStrong: string;
  text: string;
  textMuted: string;
  textSubtle: string;
  chrome: string;
  chromeText: string;
  navy: string;
  navyDeep: string;
  tint: string;
}

export interface AccentTokens {
  hue: AccentKey;
  main: string;
  soft: string;
  on: string;
}

export interface DensityTokens {
  rowY: number;
  cardPad: number;
  gap: number;
  title: number;
  body: number;
  caption: number;
  kpi: number;
  radius: number;
}

export interface HomeVariant {
  label: string;
  desc: string;
}

// ─── Tokens ──────────────────────────────────────────────────────────

export const THEMES: Record<ThemeName, ThemeTokens> = {
  light: {
    bg: '#f5f6f8',
    surface: '#ffffff',
    surface2: '#fafbfc',
    surfaceHover: '#f5f6f8',
    border: 'rgba(15,39,68,0.08)',
    borderStrong: 'rgba(15,39,68,0.16)',
    text: '#0a1628',
    textMuted: '#526079',
    textSubtle: '#8695ac',
    chrome: '#0f2744',
    chromeText: '#ffffff',
    navy: '#0f2744',
    navyDeep: '#081a30',
    tint: 'rgba(15,39,68,0.04)',
  },
  dark: {
    bg: '#0a1220',
    surface: '#111c2e',
    surface2: '#162336',
    surfaceHover: '#1a2840',
    border: 'rgba(255,255,255,0.08)',
    borderStrong: 'rgba(255,255,255,0.16)',
    text: '#e8eef7',
    textMuted: '#94a3b8',
    textSubtle: '#64748b',
    chrome: '#081321',
    chromeText: '#e8eef7',
    navy: '#1e3a5f',
    navyDeep: '#0f2744',
    tint: 'rgba(255,255,255,0.03)',
  },
};

export const ACCENTS: Record<AccentKey, AccentTokens> = {
  amber: { hue: 'amber', main: '#f59e0b', soft: '#fef3c7', on: '#78350f' },
  coral: { hue: 'coral', main: '#ef4444', soft: '#fee2e2', on: '#7f1d1d' },
  electric: { hue: 'electric', main: '#3b82f6', soft: '#dbeafe', on: '#1e3a8a' },
  emerald: { hue: 'emerald', main: '#10b981', soft: '#d1fae5', on: '#064e3b' },
  violet: { hue: 'violet', main: '#8b5cf6', soft: '#ede9fe', on: '#4c1d95' },
};

export const DENSITY: Record<DensityKey, DensityTokens> = {
  compact: { rowY: 6, cardPad: 10, gap: 8, title: 13, body: 12, caption: 10, kpi: 20, radius: 10 },
  comfortable: {
    rowY: 16,
    cardPad: 20,
    gap: 18,
    title: 18,
    body: 15,
    caption: 12,
    kpi: 30,
    radius: 16,
  },
};

// Variants for the Home/Dashboard. Layout directions, not separate themes.
export const HOME_VARIANTS: Record<HomeVariantKey, HomeVariant> = {
  control: { label: 'Control Room', desc: 'Dense, data-first — political war room' },
  editorial: { label: 'Editorial Brief', desc: 'Typography-led, serious tone' },
  intel: { label: 'Intel Cards', desc: 'Airy card-based — clean SaaS' },
};

// ─── Fonts (web-leaning) ─────────────────────────────────────────────

export const FONT_STACK = {
  sans: 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif',
  mono: '"JetBrains Mono", "SF Mono", ui-monospace, monospace',
  armenian: '"Noto Sans Armenian"',
  hebrew: '"Noto Sans Hebrew"',
} as const;

// Multi-family stack used in `fontFamily` style props so Armenian / Hebrew /
// Cyrillic glyphs fall back naturally. RN consumers will ignore this and
// resolve fonts via expo-font instead.
export const fullFontStack =
  'Inter, "Noto Sans Armenian", "Noto Sans Hebrew", -apple-system, "Segoe UI", system-ui, sans-serif';
